import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function parseEventDate(eventDateRaw: string): string | null {
  const [datePart] = eventDateRaw.split("T");

  const yyyymmdd = dayjs(datePart, "YYYYMMDD", true);
  if (yyyymmdd.isValid()) {
    return yyyymmdd.format("YYYY-MM-DD");
  }

  const ddmmyyyy = dayjs(datePart, "DDMMYYYY", true);
  if (ddmmyyyy.isValid()) {
    return ddmmyyyy.format("YYYY-MM-DD");
  }

  const ddmmyyyy_slash = dayjs(datePart, "DD/MM/YYYY", true);
  if (ddmmyyyy_slash.isValid()) {
    return ddmmyyyy_slash.format("YYYY-MM-DD");
  }

  const isoDate = dayjs(datePart, "YYYY-MM-DD", true);
  if (isoDate.isValid()) {
    return isoDate.format("YYYY-MM-DD");
  }

  const fallback = dayjs(eventDateRaw);
  return fallback.isValid() ? fallback.format("YYYY-MM-DD") : null;
}

export function normalizeEventTime(eventTime: string): string {
  const trimmedTime = eventTime?.trim();
  if (!trimmedTime) {
    return "00:00:00";
  }

  if (/^\d{1,2}:\d{2}$/.test(trimmedTime)) {
    return `${trimmedTime}:00`;
  }

  return trimmedTime;
}

/**
 * WordPress stores event_time as UTC (H:i:s) and combines it with event_date.
 * Matches the previous useConvertTimeToLocal parsing behaviour.
 */
export function buildEventDateTime(
  eventDate: string,
  eventTime: string,
  eventTimeZone: string = ""
): dayjs.Dayjs | null {
  if (!eventDate || !ISO_DATE_PATTERN.test(eventDate)) {
    return null;
  }

  const normalizedTime = normalizeEventTime(eventTime);
  const timeZoneOffset = eventTimeZone ? "00:00" : "01:00";
  const targetTimeZone = eventTimeZone || "Europe/London";

  try {
    const eventDateTime = dayjs(
      `${eventDate}T${normalizedTime}+${timeZoneOffset}`
    ).tz(targetTimeZone);

    return eventDateTime.isValid() ? eventDateTime : null;
  } catch {
    return null;
  }
}
